class XomdMediaCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.track = this.querySelector('[data-carousel-track]');
    this.previousButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.previousButton?.addEventListener('click', () => this.move(-1));
    this.nextButton?.addEventListener('click', () => this.move(1));
    this.track?.addEventListener('scroll', () => this.updateControls(), { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    if (this.track) this.resizeObserver.observe(this.track);
    this.querySelectorAll('[data-video-play]').forEach((button) => {
      button.addEventListener('click', () => this.playVideo(button));
    });
    requestAnimationFrame(() => this.updateControls());
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  updateControls() {
    if (!this.track) return;
    const maxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
  }

  move(direction) {
    const card = this.track?.querySelector('[data-carousel-card]');
    if (!card) return;
    this.querySelectorAll('video').forEach((video) => this.resetVideo(video));
    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || 0);
    this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
  }

  resetVideo(video) {
    video.pause();
    video.controls = false;
    const button = video.closest('[data-carousel-card]')?.querySelector('[data-video-play]');
    if (button) button.hidden = false;
  }

  async playVideo(button) {
    const card = button.closest('[data-carousel-card]');
    const video = card?.querySelector('video');
    if (!video) return;
    this.querySelectorAll('video').forEach((other) => {
      if (other !== video) this.resetVideo(other);
    });
    button.hidden = true;
    video.controls = true;
    video.addEventListener('ended', () => this.resetVideo(video), { once: true });
    try { await video.play(); } catch (_) { button.hidden = false; }
  }
}

class XomdTestimonialSlideshow extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.slides = [...this.querySelectorAll('[data-testimonial-slide]')];
    this.counter = this.querySelector('[data-testimonial-current]');
    this.index = 0;
    this.querySelector('[data-testimonial-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-testimonial-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    this.show(0);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  show(index) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      slide.hidden = slideIndex !== this.index;
      slide.setAttribute('aria-hidden', slideIndex === this.index ? 'false' : 'true');
    });
    if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.dataset.autoplay !== 'true' || this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = Math.max(Number.parseInt(this.dataset.interval || '6000', 10), 4000);
    this.autoplayTimer = window.setInterval(() => this.show(this.index + 1), interval);
  }

  stopAutoplay() {
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = undefined;
  }
}

if (!customElements.get('xomd-media-carousel')) customElements.define('xomd-media-carousel', XomdMediaCarousel);
if (!customElements.get('xomd-testimonial-slideshow')) customElements.define('xomd-testimonial-slideshow', XomdTestimonialSlideshow);
